// =============================================================================
// UNIFIED PRICING - Single Source of Truth
// All pricing flows from this file. Update here = updates everywhere.
// =============================================================================

import { PricingConfig } from '../types/pricing';
import { supabase, requireTenantId } from '../lib/supabase';

// Cache for Supabase pricing to avoid repeated fetches
let supabasePricingCache: PricingConfig | null = null;
let lastFetchTime = 0;
const CACHE_DURATION = 5000; // 5 seconds

// DEFAULT PRICING - can be overridden via Admin Dashboard
import { DEFAULT_PRICING } from './defaultPricing'


// Extended pricing data (not in PricingConfig type)
const EXTENDED_PRICING = {
  woodUpgrades: {
    'Twin/Twin': { maple: 450, whiteOak: 750, walnut: 1500 },
    'Twin/Full': { maple: 475, whiteOak: 800, walnut: 1575 },
    'Full/Full': { maple: 525, whiteOak: 875, walnut: 1725 },
    'Triple': { maple: 675, whiteOak: 1125, walnut: 2250 },
    'Quad': { maple: 900, whiteOak: 1500, walnut: 3000 },
    'L-Shaped': { maple: 725, whiteOak: 1200, walnut: 2400 },
    'Queen/Queen': { maple: 800, whiteOak: 1325, walnut: 2650 },
    'Twin XL/Queen': { maple: 650, whiteOak: 1100, walnut: 2200 }
  },
  
  addOnDetails: {
    'staircase_3_drawers': {
      label: 'Staircase without Drawers',
      internalCost: 529,
      clientPrice: 1895,
      laborHours: 8
    },
    'staircase_blum': {
      label: 'Staircase with Soft-Close Drawers',
      internalCost: 664,
      clientPrice: 2370,
      laborHours: 8
    },
    'trundle': {
      label: 'Trundle Bed',
      internalCost: 237,
      clientPrice: 850,
      laborHours: 4
    },
    'slide': {
      label: 'Slide',
      internalCost: 461,
      clientPrice: 1650,
      laborHours: 2.5
    },
    'desk': {
      label: 'Built-in Desk',
      internalCost: 305,
      clientPrice: 1095,
      laborHours: 5
    },
    'led_lighting': {
      label: 'LED Lighting Package',
      internalCost: 158,
      clientPrice: 565,
      laborHours: 2
    },
    'storage_drawers_2': {
      label: 'Under-bed Storage (2 drawers)',
      internalCost: 220,
      clientPrice: 800,
      laborHours: 4
    },
    'storage': {
      label: 'Under-bed Storage Drawers',
      internalCost: 220,
      clientPrice: 800,
      laborHours: 4
    },
    'storage_drawers': {
      label: 'Under-bed Storage Drawers',
      internalCost: 220,
      clientPrice: 800,
      laborHours: 4
    },
    'upholstery': {
      label: 'Custom Upholstered Panels',
      internalCost: 800,
      clientPrice: 2000,
      laborHours: 6
    },
    'carcass': {
      label: 'Custom Carcass',
      internalCost: 140,
      clientPrice: 350,
      laborHours: 3
    }
  },
  
  deliveryBreakeven: 195,
  
  install: {
    none: 0,
    standard: 850,      // Single set (Twin/Twin, Twin/Full, Full/Full)
    mid: 1200,          // Mid-size (Twin XL/Queen, Queen/Queen)
    complex: 1800,      // Triple bunk
    full: 2500          // Quad installation
  },
  
  installBreakeven: 1200,
  
  commission: {
    baseRate: 0.10,     // 10% base commission
    bonusThreshold: 15000,
    bonusRate: 0.02     // Additional 2% for orders over threshold
  }
};

// =============================================================================
// PRICING FUNCTIONS
// =============================================================================

/**
 * Get current pricing configuration
 * Uses in-memory cache synced from Supabase (source of truth)
 */
export function getPricing(): PricingConfig {
  // Use in-memory cache if available (synced from Supabase)
  if (supabasePricingCache) {
    return deepMerge(DEFAULT_PRICING, supabasePricingCache);
  }
  // Return defaults if cache not yet loaded
  return DEFAULT_PRICING;
}

/**
 * Save pricing configuration (from Admin)
 * Saves to Supabase (source of truth) and updates in-memory cache
 */
export function savePricing(updates: Partial<PricingConfig>): PricingConfig {
  const current = getPricing();
  const updated = deepMerge(current, {
    ...updates,
    lastUpdated: new Date().toISOString()
  });
  
  // Update in-memory cache immediately
  supabasePricingCache = updated;
  
  // Save to Supabase (source of truth)
  savePricingToSupabase(updated);
  
  return updated;
}

/**
 * Save pricing to Supabase for real-time sync
 */
export async function savePricingToSupabase(pricing: PricingConfig): Promise<boolean> {
  try {
    const prTid = await requireTenantId();
    const { error } = await supabase
      .from('settings')
      .upsert({
        tenant_id: prTid,
        key: 'pricing_config',
        value: pricing,
        updated_at: new Date().toISOString()
      }, { onConflict: 'key' });
    
    if (error) {
      console.error('Failed to save pricing to Supabase:', error);
      return false;
    }
    
    console.log('📊 Pricing saved to Supabase for real-time sync');
    supabasePricingCache = pricing;
    lastFetchTime = Date.now();
    return true;
  } catch (e) {
    console.error('Error saving pricing to Supabase:', e);
    return false;
  }
}

/**
 * Load pricing from Supabase (for partners portal)
 */
export async function loadPricingFromSupabase(): Promise<PricingConfig | null> {
  // Return cached if fresh
  if (supabasePricingCache && (Date.now() - lastFetchTime) < CACHE_DURATION) {
    return supabasePricingCache;
  }
  
  try {
    const { data, error } = await supabase
      .from('settings')
      .select('value')
      .eq('key', 'pricing_config')
      .maybeSingle();
    
    if (error || !data) {
      return null;
    }
    
    supabasePricingCache = deepMerge(DEFAULT_PRICING, data.value);
    lastFetchTime = Date.now();
    return supabasePricingCache;
  } catch (e) {
    console.error('Error loading pricing from Supabase:', e);
    return null;
  }
}

/**
 * Clear pricing cache (call when real-time update received)
 */
export function clearPricingCache(): void {
  supabasePricingCache = null;
  lastFetchTime = 0;
}

/**
 * Reset pricing to defaults
 */
export function resetPricing(): PricingConfig {
  // Clear in-memory cache
  supabasePricingCache = null;
  // Reset in Supabase (source of truth)
  savePricingToSupabase(DEFAULT_PRICING);
  return DEFAULT_PRICING;
}

/**
 * Calculate client price from internal cost
 */
export function calculateClientPrice(internalCost: number, markup: number | null = null): number {
  const pricing = getPricing();
  const m = markup || pricing.markup;
  return Math.round(internalCost * m);
}

/**
 * Get config price (product only, before delivery/install)
 */
export function getConfigPrice(configName: string, woodType: string = 'poplar'): number {
  const pricing = getPricing();
  const config = pricing.configs[configName];
  if (!config) return 0;

  let price = config.clientPrice;

  // Apply wood upgrade
  if (woodType && woodType !== 'poplar' && woodType !== 'pine') {
    const woodKey = woodType === 'hardMaple' ? 'maple' : woodType;
    const upgrade = EXTENDED_PRICING.woodUpgrades[configName]?.[woodKey] || 0;
    price += upgrade;
  }

  return price;
}

/**
 * Calculate fabrication payout from BOM operation counts
 * Uses rates from Admin Pricing instead of hardcoded percentage
 */
export function calculateFabPayoutFromBOM(bomData: {
  ripCuts?: number;
  crossCuts?: number;
  edgeBanding?: number;  // linear feet
  countersunkHoles?: number;
  pocketHoles?: number;
  bedCount?: number;  // number of bed units in order
}): number {
  const pricing = getPricing();
  const rates = pricing.fabLaborRates;

  if (!rates) {
    console.warn('[Payout] No fabLaborRates found in pricing config');
    return 0;
  }

  console.log('[Payout] BOM Data:', bomData);
  console.log('[Payout] Labor Rates:', rates);

  // Calculate labor cost for each operation
  const ripCutCost = (bomData.ripCuts || 0) * (rates.ripCut || 0);
  const crossCutCost = (bomData.crossCuts || 0) * (rates.crossCut || 0);
  const edgeBandingCost = (bomData.edgeBanding || 0) * (rates.edgeBanding || 0);
  const countersunkCost = (bomData.countersunkHoles || 0) * (rates.countersunkHole || 0);
  const pocketHoleCost = (bomData.pocketHoles || 0) * (rates.pocketHole || 0);

  // Base assembly fee per bed unit
  const bedCount = bomData.bedCount || 1;
  const assemblyCost = bedCount * (rates.baseAssembly || 0);

  console.log('[Payout] Cost breakdown:', {
    ripCutCost,
    crossCutCost,
    edgeBandingCost,
    countersunkCost,
    pocketHoleCost,
    assemblyCost
  });

  // Total payout
  const total = ripCutCost + crossCutCost + edgeBandingCost +
                countersunkCost + pocketHoleCost + assemblyCost;

  console.log('[Payout] Total:', total);

  return Math.round(total);
}

/**
 * Extract BOM operation counts from a BOM object
 * Used to avoid circular dependency with bomTemplates.ts
 */
export function extractBOMOperations(bom: any): {
  ripCuts: number;
  crossCuts: number;
  edgeBanding: number;
  countersunkHoles: number;
  pocketHoles: number;
  bedCount: number;
} {
  if (!bom) {
    console.warn('[BOM Extract] No BOM provided');
    return {
      ripCuts: 0,
      crossCuts: 0,
      edgeBanding: 0,
      countersunkHoles: 0,
      pocketHoles: 0,
      bedCount: 0
    };
  }

  console.log('[BOM Extract] BOM cuts data:', bom.cuts);
  console.log('[BOM Extract] BOM totalBeds:', bom.totalBeds);

  // Extract hardware hole counts
  // NOTE: This is an estimation - screw count doesn't always equal hole count
  let countersunkHoles = 0;
  let pocketHoles = 0;

  if (bom.hardware && Array.isArray(bom.hardware)) {
    bom.hardware.forEach((item: any) => {
      const name = item.name?.toLowerCase() || '';
      if (name.includes('trim screw') || name.includes('countersunk')) {
        countersunkHoles += item.qty || 0;
      }
      if (name.includes('pocket screw')) {
        pocketHoles += item.qty || 0;
      }
    });
  }

  const result = {
    ripCuts: bom.cuts?.plywoodRips || 0,
    crossCuts: bom.cuts?.lumberCrossCuts || 0,
    edgeBanding: bom.cuts?.edgeBanding || 0,
    countersunkHoles,
    pocketHoles,
    bedCount: bom.totalBeds || bom.bedCount || 0
  };

  console.log('[BOM Extract] Extracted operations:', result);

  return result;
}

/**
 * Calculate shipping and packaging costs for job costing
 * @param bedCount - Number of bed units in the order
 * @param zone - Shipping zone: 'local' | 'regional' | 'midRange' | 'national'
 * @returns Object with cost breakdown and totals
 */
export function calculateShippingCosts(
  bedCount: number = 1,
  zone: 'local' | 'regional' | 'midRange' | 'national' = 'regional'
): {
  ltlFreight: number;
  packaging: number;
  totalCost: number;
  chargeToClient: number;
  markup: number;
  breakdown: {
    cardboard: number;
    bubbleWrap: number;
    shrinkWrap: number;
    pallets: number;
    strapping: number;
    labels: number;
  };
} {
  const pricing = getPricing() as any;
  const shippingConfig = pricing.shippingCosts || (DEFAULT_PRICING as any).shippingCosts;
  
  // Base LTL freight cost
  const ltlFreight = shippingConfig.ltlBase?.[zone] || 300;
  
  // Packaging materials per bed
  const packagingPerBed = shippingConfig.packagingPerBed || 92;
  const packaging = packagingPerBed * bedCount;
  
  // Breakdown
  const breakdown = {
    cardboard: (shippingConfig.packaging?.cardboard || 25) * bedCount,
    bubbleWrap: (shippingConfig.packaging?.bubbleWrap || 15) * bedCount,
    shrinkWrap: (shippingConfig.packaging?.shrinkWrap || 10) * bedCount,
    pallets: (shippingConfig.packaging?.pallets || 35) * Math.ceil(bedCount / 2), // 2 beds per pallet
    strapping: (shippingConfig.packaging?.strapping || 5) * bedCount,
    labels: (shippingConfig.packaging?.labels || 2) * bedCount
  };
  
  // Total internal cost
  const totalCost = ltlFreight + packaging;
  
  // Apply markup for client pricing
  const markupRate = shippingConfig.markup || 1.67;
  const chargeToClient = Math.round(totalCost * markupRate);
  
  return {
    ltlFreight,
    packaging,
    totalCost,
    chargeToClient,
    markup: markupRate,
    breakdown
  };
}

/**
 * Calculate fabrication payout from BOM data
 *
 * To avoid circular dependencies, call this with BOM data from generateBOMFromFloorPlan():
 *
 * @example
 * import { generateBOMFromFloorPlan } from './cutlist/bomTemplates';
 * import { calculateOrderFabPayoutFromBOM } from './pricing';
 *
 * const bom = generateBOMFromFloorPlan(placedBeds, options);
 * const payout = calculateOrderFabPayoutFromBOM(bom);
 */
export function calculateOrderFabPayoutFromBOM(bom: any): number {
  const bomOps = extractBOMOperations(bom);
  return calculateFabPayoutFromBOM(bomOps);
}

/**
 * Get config internal cost
 */
export function getConfigInternalCost(configName: string): number {
  const pricing = getPricing();
  return pricing.configs[configName]?.internalCost || 0;
}

/**
 * Get full price (product + delivery + install)
 */
export function getFullPrice(
  configName: string, 
  woodType: string = 'poplar', 
  deliveryZone: 'local' | 'regional' | 'national' = 'local', 
  wantsInstall: boolean = true
): number {
  const pricing = getPricing();
  
  let price = getConfigPrice(configName, woodType);
  
  // Add delivery
  price += pricing.delivery?.[deliveryZone] || 0;
  
  // Add installation based on config complexity
  if (wantsInstall) {
    const installTier = getInstallTier(configName);
    price += EXTENDED_PRICING.install[installTier] || 0;
  }
  
  return price;
}

/**
 * Get installation tier for a config
 */
export function getInstallTier(configName: string): 'none' | 'standard' | 'mid' | 'complex' | 'full' {
  const tierMap: Record<string, 'none' | 'standard' | 'mid' | 'complex' | 'full'> = {
    'Twin/Twin': 'standard',
    'Twin/Full': 'standard',
    'Full/Full': 'standard',
    'Twin XL/Queen': 'mid',
    'Queen/Queen': 'mid',
    'Triple': 'complex',
    'Quad': 'full',
    'L-Shaped': 'complex'
  };
  return tierMap[configName] || 'standard';
}

/**
 * Get add-on price
 */
export function getAddOnPrice(addOnId: string): number {
  return EXTENDED_PRICING.addOnDetails[addOnId]?.clientPrice || 0;
}

/**
 * Get add-on internal cost
 */
export function getAddOnInternalCost(addOnId: string): number {
  return EXTENDED_PRICING.addOnDetails[addOnId]?.internalCost || 0;
}

/**
 * Get all add-ons as array for dropdowns/lists
 */
export function getAddOnsList() {
  return Object.entries(EXTENDED_PRICING.addOnDetails).map(([id, data]) => ({
    id,
    label: data.label,
    price: data.clientPrice,
    internalCost: data.internalCost,
    laborHours: data.laborHours
  }));
}

/**
 * Get all configs as array for dropdowns/lists
 * Returns configs grouped by type (bunk vs loft)
 */
export function getConfigsList() {
  const pricing = getPricing();
  return Object.entries(pricing.configs).map(([name, data]) => {
    const isLoft = name.toLowerCase().includes('loft');
    return {
      id: name.toLowerCase().replace(/\//g, '-').replace(/\s+/g, '-'),
      name,
      label: name, // Already includes "Bunk" or "Loft" as appropriate
      type: isLoft ? 'loft' : 'bunk',
      price: data.clientPrice,
      fullPrice: data.fullPrice,
      internalCost: data.internalCost,
      laborHours: data.laborHours
    };
  });
}

/**
 * Get configs grouped by type for optgroup rendering
 */
export function getGroupedConfigs() {
  const all = getConfigsList();
  return {
    bunks: all.filter(c => c.type === 'bunk'),
    lofts: all.filter(c => c.type === 'loft')
  };
}

/**
 * Get material cost
 */
export function getMaterialCost(category: string, item: string): number {
  const pricing = getPricing();
  const materials = pricing.materials as any;
  return materials[category]?.[item] || 0;
}

/**
 * Calculate profit margin
 */
export function calculateMargin(revenue: number, cost: number): string {
  if (revenue <= 0) return '0';
  return ((revenue - cost) / revenue * 100).toFixed(1);
}

/**
 * Calculate commission
 */
export function calculateCommission(totalPrice: number) {
  const base = totalPrice * EXTENDED_PRICING.commission.baseRate;
  const bonus = totalPrice > EXTENDED_PRICING.commission.bonusThreshold 
    ? totalPrice * EXTENDED_PRICING.commission.bonusRate 
    : 0;
  return {
    base,
    bonus,
    total: base + bonus,
    rate: EXTENDED_PRICING.commission.baseRate + (bonus > 0 ? EXTENDED_PRICING.commission.bonusRate : 0)
  };
}

// =============================================================================
// QUICK QUOTE AI - Configuration Matching
// =============================================================================

// OUTER BED BOX dimensions (inches)
// These are proprietary dimensions for our fabricated bed boxes (NOT mattress sizes)
const MATTRESS_DIMENSIONS = {
  'Twin': { width: 43.375, length: 77 },
  'Twin XL': { width: 43.375, length: 82 },
  'Full': { width: 58.375, length: 77 },
  'Queen': { width: 64.375, length: 82 },
  'King': { width: 76, length: 80 },      // TODO: Update when King is fabricated
  'Cal King': { width: 72, length: 84 }   // TODO: Update when Cal King is fabricated
};

interface ConfigMatch {
  configName: string;
  configId: string;
  confidence: number;
  mattressCounts: Record<string, number>;
  pricing: {
    internalCost: number;
    clientPrice: number;
    fullPrice: number;
  } | null;
  requiresReview: boolean;
  matchDetails: {
    inputWidth: number | null;
    inputLength: number | null;
    inputMattresses: string[];
    normalizedMattresses: string[];
  };
}

/**
 * Match extracted dimensions to closest standard bed configuration
 * Used by Quick Quote AI to map design plan analysis to pricing configs
 */
export function matchConfigFromDimensions(
  width: number | null, 
  length: number | null, 
  mattressTypes: string[] = [], 
  options: any = {}
): ConfigMatch {
  const pricing = getPricing();
  const configs = Object.keys(pricing.configs);
  
  // Normalize mattress types
  const normalizedMattresses = mattressTypes.map(m => {
    const lower = (m || '').toLowerCase().trim();
    if (lower.includes('twin') && lower.includes('xl')) return 'Twin XL';
    if (lower.includes('twin')) return 'Twin';
    if (lower.includes('full')) return 'Full';
    if (lower.includes('queen')) return 'Queen';
    if (lower.includes('king') && lower.includes('cal')) return 'Cal King';
    if (lower.includes('king')) return 'King';
    return m;
  });

  // Count mattress types
  const mattressCounts: Record<string, number> = {};
  normalizedMattresses.forEach(m => {
    mattressCounts[m] = (mattressCounts[m] || 0) + 1;
  });

  // Determine best config match based on mattress types
  let bestMatch: string | null = null;
  let confidence = 0;

  // Direct mattress-based matching
  if (mattressCounts['Queen'] >= 2) {
    bestMatch = 'Queen/Queen';
    confidence = 0.95;
  } else if (mattressCounts['Queen'] && (mattressCounts['Twin XL'] || mattressCounts['Twin'])) {
    bestMatch = 'Twin XL/Queen';
    confidence = 0.9;
  } else if (mattressCounts['Queen'] && !mattressCounts['Twin'] && !mattressCounts['Full']) {
    // Single queen likely means loft-over-queen
    bestMatch = 'Twin XL/Queen';
    confidence = 0.75;
  } else if (mattressCounts['Full'] >= 2) {
    bestMatch = 'Full/Full';
    confidence = 0.95;
  } else if (mattressCounts['Full'] && mattressCounts['Twin']) {
    bestMatch = 'Twin/Full';
    confidence = 0.95;
  } else if (mattressCounts['Twin'] >= 4) {
    bestMatch = 'Quad';
    confidence = 0.9;
  } else if (mattressCounts['Twin'] >= 3 || mattressCounts['Full'] >= 3) {
    bestMatch = 'Triple';
    confidence = 0.85;
  } else if (mattressCounts['Twin'] >= 2) {
    bestMatch = 'Twin/Twin';
    confidence = 0.9;
  }

  // Fallback to dimension-based matching if no mattress info
  if (!bestMatch && width && length) {
    // Width-based estimation
    if (width >= 70) {
      bestMatch = 'Queen/Queen';
      confidence = 0.6;
    } else if (width >= 60) {
      bestMatch = 'Twin XL/Queen';
      confidence = 0.6;
    } else if (width >= 54) {
      bestMatch = 'Full/Full';
      confidence = 0.6;
    } else if (width >= 45) {
      bestMatch = 'Twin/Full';
      confidence = 0.6;
    } else {
      bestMatch = 'Twin/Twin';
      confidence = 0.5;
    }
  }

  // Default if nothing matched
  if (!bestMatch) {
    bestMatch = 'Twin/Twin';
    confidence = 0.3;
  }

  // Get pricing for matched config
  const configData = pricing.configs[bestMatch];
  
  return {
    configName: bestMatch,
    configId: bestMatch.toLowerCase().replace(/[\/\s]+/g, '-'),
    confidence,
    mattressCounts,
    pricing: configData ? {
      internalCost: configData.internalCost,
      clientPrice: configData.clientPrice,
      fullPrice: configData.fullPrice
    } : null,
    requiresReview: confidence < 0.7,
    matchDetails: {
      inputWidth: width,
      inputLength: length,
      inputMattresses: mattressTypes,
      normalizedMattresses
    }
  };
}

/**
 * Get all available configurations for Quick Quote dropdown
 */
export function getAvailableConfigs() {
  const pricing = getPricing();
  return Object.entries(pricing.configs).map(([name, data]) => {
    const isLoft = name.toLowerCase().includes('loft');
    const isSpecial = ['Triple', 'Quad', 'L-Shaped'].includes(name);
    return {
      name,
      id: name.toLowerCase().replace(/[\/\s]+/g, '-'),
      type: isLoft ? 'loft' : (isSpecial ? 'special' : 'bunk'),
      clientPrice: data.clientPrice,
      fullPrice: data.fullPrice,
      internalCost: data.internalCost
    };
  });
}

/**
 * Calculate Quick Quote price with add-ons
 */
export function calculateQuickQuotePrice(configName: string, addOns: string[] = [], woodType: string = 'birch') {
  const pricing = getPricing();
  const configData = pricing.configs[configName];
  
  if (!configData) {
    return null;
  }

  let total = configData.clientPrice;

  // Add wood upgrade cost
  const woodUpgrade = EXTENDED_PRICING.woodUpgrades?.[configName]?.[woodType] || 0;
  total += woodUpgrade;

  // Add add-on costs
  addOns.forEach(addon => {
    const addonData = EXTENDED_PRICING.addOnDetails[addon];
    if (addonData?.clientPrice) {
      total += addonData.clientPrice;
    }
  });

  // Get delivery and install costs
  const delivery = pricing.delivery?.local || 195;
  const install = EXTENDED_PRICING.install?.standard || 1200;

  return {
    productPrice: total,
    delivery,
    install,
    totalWithServices: total + delivery + install,
    breakdown: {
      baseConfig: configData.clientPrice,
      woodUpgrade,
      addOns: addOns.map(a => ({ 
        name: a, 
        price: EXTENDED_PRICING.addOnDetails[a]?.clientPrice || 0 
      }))
    }
  };
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Deep merge two objects
 */
function deepMerge(target: any, source: any): any {
  const result = { ...target };
  
  for (const key in source) {
    if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
      result[key] = deepMerge(target[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  }
  
  return result;
}

// Export defaults for reference
export { DEFAULT_PRICING };
